import { Prisma } from '@prisma/client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { BulkMutateResult, MutateResult } from '@app/domain-interfaces';
import {
  BulkCreateSubscriptionDto,
  CreateSubscriptionDto,
  EditSubscriptionDto,
  FindSubscriptionsResult,
  SubscriptionWithChildren,
} from '@app/domain-subscription-interfaces';
import { CommonFindResult } from '@app/infra-interfaces';
import { Axios } from '@app/web-components';

import { subscriptionAccessKeys } from './subscriptions-access';

const SUBSCRIPTION_ALL_QUERY_KEY = 'SUBSCRIPTION_ALL_QUERY_KEY';
const RESOURCE_URL = '/subscriptions';

export const subscriptionKeys = {
  all: [SUBSCRIPTION_ALL_QUERY_KEY] as const,
  lists: () => [...subscriptionKeys.all, 'list'] as const,
  list: (filters: object) => [...subscriptionKeys.lists(), filters] as const,
};

/** use when unable to use the useFindSubscription hook */
export const useFindSubscriptionFetch = () => {
  const axios = Axios.useAxiosApi();
  const queryClient = useQueryClient();

  const findSubscription = (params: Prisma.SubscriptionFindManyArgs) => {
    return queryClient.fetchQuery<FindSubscriptionsResult>({
      queryKey: subscriptionKeys.list(params),
      queryFn: async () => {
        const { data } = await axios.post(`${RESOURCE_URL}/find`, params);
        return data;
      },
    });
  };

  return findSubscription;
};

export const useFindSubscription = <T>(params: Prisma.SubscriptionFindManyArgs) => {
  const axios = Axios.useAxiosApi();

  return useQuery<CommonFindResult<T>>({
    queryKey: subscriptionKeys.list(params),
    queryFn: async () => {
      const { data } = await axios.post(`${RESOURCE_URL}/find`, params);
      return data;
    },
  });
};

export const useFindSubscriptionById = ({
  subscriptionId,
  enabled = true,
}: {
  subscriptionId: number;
  enabled?: boolean;
}) => {
  const axios = Axios.useAxiosApi();

  return useQuery<SubscriptionWithChildren>({
    queryKey: subscriptionKeys.list({ subscriptionId }),
    queryFn: async () => {
      const { data } = await axios.get(`${RESOURCE_URL}/${subscriptionId}`);
      return data;
    },
    enabled,
  });
};

export const useBulkCreateSubscription = ({ validateOnly = false }: { validateOnly?: boolean } = {}) => {
  const axios = Axios.useAxiosApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: BulkCreateSubscriptionDto) => {
      return axios.post<BulkMutateResult<SubscriptionWithChildren>>(`${RESOURCE_URL}/bulk`, {
        ...dto,
        validateOnly,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SUBSCRIPTION_ALL_QUERY_KEY] });
    },
  });
};

export const useCreateSubscription = ({ validateOnly = false }: { validateOnly?: boolean } = {}) => {
  const axios = Axios.useAxiosApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: CreateSubscriptionDto) => {
      return axios.post<MutateResult<SubscriptionWithChildren>>(`${RESOURCE_URL}`, {
        ...dto,
        validateOnly,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SUBSCRIPTION_ALL_QUERY_KEY] });
    },
  });
};

export const useCancelSubscription = () => {
  const axios = Axios.useAxiosApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => {
      return axios.post<MutateResult<SubscriptionWithChildren>>(`${RESOURCE_URL}/cancel`, { id });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SUBSCRIPTION_ALL_QUERY_KEY] });
    },
  });
};

export const useEditSubscription = ({ validateOnly = false }: { validateOnly?: boolean }) => {
  const axios = Axios.useAxiosApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: EditSubscriptionDto) => {
      return axios.patch<MutateResult<SubscriptionWithChildren>>(RESOURCE_URL, { ...dto, validateOnly });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SUBSCRIPTION_ALL_QUERY_KEY] });
    },
  });
};

export const useStartPullBundleAccessMembership = () => {
  const axios = Axios.useAxiosApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (bundleId: number) =>
      axios.post(`${RESOURCE_URL}/start-pull-bundle-access-membership`, { bundleId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [subscriptionKeys, subscriptionAccessKeys.all] });
    },
  });
};
